/* ruleToSparql — typed Predicate AST → SPARQL 1.1 query string.
 *
 * Spec 07 §"AST→SPARQL contract". The Builder's right rail shows
 * this preview so designers see exactly the query their rule
 * compiles to. The agent's typed evaluator is the production
 * authority; this function is the documented bridge.
 *
 * Grammar reference: <https://www.w3.org/TR/sparql11-query/>
 *
 * Conventions:
 *   - Every node kind (Room, SKU, …) lives in our local `foyr:` ns.
 *     We also emit an `rdfs:subClassOf` hint when codegen.json
 *     declares the class extends a public ontology term (schema:,
 *     bot:, gr:, …) — readers get the interop story for free.
 *   - One ASK query for boolean predicates (exists / forbidden /
 *     cmp / in_set / in_range / and / or / not).
 *   - One SELECT … HAVING for aggregation predicates (sum/avg/count/…).
 *
 * No `any`. No string concatenation hacks for unsafe input — we own
 * both sides of the AST so input is already typed by Zod at the
 * boundary. Identifiers from the AST go through `escapeLiteral` /
 * `escapeIri` defensively anyway.
 */

import type {
  Predicate,
  ExtendedTerm,
  Term,
  NodePattern,
  Comparator,
  AggTerm,
  Literal,
} from "../types/rule";
import codegen from "../../../docs/ontology/codegen.json";

const FOYR_NS = "https://foyr.hypergraphmind.com/ns#";
const RDF = "http://www.w3.org/1999/02/22-rdf-syntax-ns#";
const RDFS = "http://www.w3.org/2000/01/rdf-schema#";

interface PrefixDecl { prefix: string; iri: string }

/** Public ontology prefixes shipped in codegen.json, plus our own. */
function defaultPrefixes(): PrefixDecl[] {
  const ours: PrefixDecl[] = [
    { prefix: "foyr", iri: FOYR_NS },
    { prefix: "rdf",  iri: RDF },
    { prefix: "rdfs", iri: RDFS },
  ];
  const imported: PrefixDecl[] = (codegen.imports ?? []).map((i) => ({ prefix: i.prefix, iri: i.iri }));
  return [...ours, ...imported];
}

function escapeLiteral(v: string): string {
  // SPARQL 1.1 §19.6 — escape \ " \n \r \t.
  return v.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\t/g, "\\t");
}

function litToSparql(l: Literal): string {
  if (l.type === "number")  return `${l.value}`;
  if (l.type === "boolean") return l.value ? '"true"^^xsd:boolean' : '"false"^^xsd:boolean';
  return `"${escapeLiteral(String(l.value))}"`;
}

/** Convert "SKU.leadDays" → "leadDays" (the local property name). */
function propLocal(path: string): string {
  const dot = path.indexOf(".");
  return dot === -1 ? path : path.slice(dot + 1);
}

function classOf(path: string): string {
  const dot = path.indexOf(".");
  return dot === -1 ? path : path.slice(0, dot);
}

/* ─── Context: variable bindings shared across one query body. ──── */

interface Ctx {
  /** Class → var (e.g. "SKU" → "?sku"). Reused so cmp(SKU.x, op, SKU.y) reads one var. */
  classVar: Map<string, string>;
  /** path → var (e.g. "SKU.leadDays" → "?leadDays"). */
  pathVar: Map<string, string>;
  /** Triple lines accumulated in the WHERE body, in insertion order. */
  triples: string[];
  /** FILTER lines (cmp / in_set / in_range / NOT EXISTS / …). */
  filters: string[];
  /** Counter for anonymous fresh vars. */
  fresh: number;
}

function newCtx(): Ctx { return { classVar: new Map(), pathVar: new Map(), triples: [], filters: [], fresh: 0 }; }

function freshVar(ctx: Ctx, hint: string): string { ctx.fresh += 1; return `?${hint}${ctx.fresh}`; }

/** SKU → "sku" (all-caps acronym), BudgetLine → "budgetLine" (camelCase).
 *  Disambiguator: if the 2nd char is also upper-case, treat as acronym. */
function varStem(nodeKind: string): string {
  if (nodeKind.length >= 2 && nodeKind[1] === nodeKind[1]!.toUpperCase() && nodeKind[1] !== nodeKind[1]!.toLowerCase()) {
    return nodeKind.toLowerCase();
  }
  return `${nodeKind.charAt(0).toLowerCase()}${nodeKind.slice(1)}`;
}

/** Reserve a class-level subject var (?sku for SKU), emitting an
 *  rdf:type triple the first time the class is seen. */
function subjectVar(ctx: Ctx, nodeKind: string, filterAttr?: string, filterValue?: string | number | boolean): string {
  const cached = ctx.classVar.get(nodeKind);
  if (cached && filterAttr === undefined) return cached;
  const v = `?${varStem(nodeKind)}`;
  if (!cached) {
    ctx.triples.push(`  ${v} a foyr:${nodeKind} .`);
    ctx.classVar.set(nodeKind, v);
  }
  if (filterAttr !== undefined && filterValue !== undefined) {
    const lit = typeof filterValue === "number" ? `${filterValue}` : `"${escapeLiteral(String(filterValue))}"`;
    ctx.triples.push(`  ${v} foyr:${filterAttr} ${lit} .`);
  }
  return v;
}

/** Bind ?leadDays for path "SKU.leadDays", emitting the triple lazily. */
function fieldVar(ctx: Ctx, path: string): string {
  const cached = ctx.pathVar.get(path);
  if (cached) return cached;
  const cls = classOf(path);
  const prop = propLocal(path);
  const subj = subjectVar(ctx, cls);
  const v = `?${prop}`;
  ctx.triples.push(`  ${subj} foyr:${prop} ${v} .`);
  ctx.pathVar.set(path, v);
  return v;
}

function termToSparql(ctx: Ctx, t: ExtendedTerm): string {
  if (t.kind === "lit")   return litToSparql(t);
  if (t.kind === "field") return fieldVar(ctx, t.path);
  // AggTerm is rendered separately by aggToSubselect — should not appear here.
  return aggToSubselect(ctx, t);
}

function cmpOp(op: Comparator): string {
  if (op === "==") return "=";
  if (op === "!=") return "!=";
  return op;
}

/* ─── Aggregation — rendered as a sub-SELECT bound to a fresh var. ── */

function aggToSubselect(ctx: Ctx, agg: AggTerm): string {
  const sub = newCtx();
  const subj = subjectVar(sub, agg.over.nodeKind, agg.over.filterAttr, agg.over.filterValue);
  if (agg.via) {
    const tgt = subjectVar(sub, agg.via.target.nodeKind, agg.via.target.filterAttr, agg.via.target.filterValue);
    sub.triples.push(`  ${subj} foyr:${agg.via.relation} ${tgt} .`);
  }
  let inner: string;
  if (agg.op === "count") {
    inner = agg.distinct ? `COUNT(DISTINCT ${subj})` : `COUNT(${subj})`;
  } else {
    if (!agg.field) throw new Error(`AggTerm op=${agg.op} requires a field`);
    const fv = fieldVar(sub, agg.field.path);
    const op = agg.op.toUpperCase();
    inner = `${op}(${fv})`;
  }
  const out = freshVar(ctx, agg.op);
  const body = [...sub.triples, ...sub.filters].join("\n");
  const block = `  {\n    SELECT (${inner} AS ${out}) WHERE {\n${body.replace(/^  /gm, "    ")}\n    }\n  }`;
  ctx.triples.push(block);
  return out;
}

/* ─── Predicate compilation — walks the AST, mutates ctx. ────────── */

function compile(ctx: Ctx, p: Predicate): void {
  switch (p.kind) {
    case "cmp": {
      const l = termToSparql(ctx, p.lhs);
      const r = termToSparql(ctx, p.rhs);
      ctx.filters.push(`  FILTER (${l} ${cmpOp(p.op)} ${r})`);
      return;
    }
    case "in_range": {
      const v = termToSparql(ctx, p.value);
      ctx.filters.push(`  FILTER (${v} >= ${p.range.lo} && ${v} <= ${p.range.hi})`);
      return;
    }
    case "not_in_range": {
      const v = termToSparql(ctx, p.value);
      ctx.filters.push(`  FILTER (${v} < ${p.range.lo} || ${v} > ${p.range.hi})`);
      return;
    }
    case "in_set": {
      const v = termToSparql(ctx, p.value);
      const vals = p.set.values.map((x) =>
        typeof x === "number" ? `${x}` : `"${escapeLiteral(String(x))}"`,
      ).join(", ");
      ctx.filters.push(`  FILTER (${v} IN (${vals}))`);
      return;
    }
    case "not_in_set": {
      const v = termToSparql(ctx, p.value);
      const vals = p.set.values.map((x) =>
        typeof x === "number" ? `${x}` : `"${escapeLiteral(String(x))}"`,
      ).join(", ");
      ctx.filters.push(`  FILTER (${v} NOT IN (${vals}))`);
      return;
    }
    case "graph_pattern":
    case "exists": {
      compilePattern(ctx, p.kind === "exists" ? p.pattern.subject : p.subject,
                          p.kind === "exists" ? p.pattern.relation : p.relation,
                          p.kind === "exists" ? p.pattern.object : p.object);
      return;
    }
    case "forbidden": {
      const inner = newCtx();
      compilePattern(inner, p.pattern.subject, p.pattern.relation, p.pattern.object);
      const body = [...inner.triples, ...inner.filters].join("\n").replace(/^  /gm, "    ");
      ctx.filters.push(`  FILTER NOT EXISTS {\n${body}\n  }`);
      return;
    }
    case "clearance":
    case "sightline":
    case "work_triangle":
    case "egress_path": {
      // Spatial predicates evaluate via the Geometric Predicate Layer
      // (Seam 6) — turf.js client-side, shapely server-side. The SPARQL
      // serialiser emits a typed marker so the evaluator picks it up
      // and runs geometry against the live placement positions.
      const args = JSON.stringify(p).replace(/"/g, '\\"');
      ctx.filters.push(`  # SPATIAL ${p.kind} ${args}`);
      return;
    }
    case "and": compile(ctx, p.left); compile(ctx, p.right); return;
    case "or": {
      const left = newCtx(); compile(left, p.left);
      const right = newCtx(); compile(right, p.right);
      const lBody = [...left.triples, ...left.filters].join("\n").replace(/^  /gm, "    ");
      const rBody = [...right.triples, ...right.filters].join("\n").replace(/^  /gm, "    ");
      ctx.triples.push(`  {\n${lBody}\n  } UNION {\n${rBody}\n  }`);
      return;
    }
    case "not": {
      const inner = newCtx(); compile(inner, p.inner);
      const body = [...inner.triples, ...inner.filters].join("\n").replace(/^  /gm, "    ");
      ctx.filters.push(`  FILTER NOT EXISTS {\n${body}\n  }`);
      return;
    }
  }
}

function compilePattern(ctx: Ctx, subject: NodePattern, relation: string, object: NodePattern | Term): void {
  const sVar = subjectVar(ctx, subject.nodeKind, subject.filterAttr, subject.filterValue);
  if (object.kind === "pattern") {
    const oVar = subjectVar(ctx, object.nodeKind, object.filterAttr, object.filterValue);
    ctx.triples.push(`  ${sVar} foyr:${relation} ${oVar} .`);
  } else if (object.kind === "lit") {
    ctx.triples.push(`  ${sVar} foyr:${relation} ${litToSparql(object)} .`);
  } else {
    // FieldRef object — bind to a variable.
    const oVar = fieldVar(ctx, object.path);
    ctx.triples.push(`  ${sVar} foyr:${relation} ${oVar} .`);
  }
}

/** PREFIX block — only emit prefixes the query actually uses, so the
 *  output stays readable. We always include `foyr`. */
function prefixBlock(usedPrefixes: ReadonlySet<string>): string {
  const decls = defaultPrefixes().filter((p) => p.prefix === "foyr" || usedPrefixes.has(p.prefix));
  return decls.map((d) => `PREFIX ${d.prefix}: <${d.iri}>`).join("\n");
}

function scanPrefixes(query: string): Set<string> {
  const found = new Set<string>();
  const m = query.matchAll(/\b([a-z][a-z0-9]*):[A-Za-z_][\w-]*/g);
  for (const mm of m) {
    const p = mm[1];
    if (p) found.add(p);
  }
  return found;
}

export interface SparqlResult {
  /** Full SPARQL 1.1 query (with PREFIX block). */
  query: string;
  /** ASK if the predicate is boolean, SELECT if aggregation present. */
  form: "ASK" | "SELECT";
}

export function ruleToSparql(p: Predicate): SparqlResult {
  const ctx = newCtx();
  compile(ctx, p);
  const body = [...ctx.triples, ...ctx.filters].join("\n");
  // We use ASK for boolean predicates — designers read "is this true
  // for the project". The agent's typed evaluator can substitute
  // SELECT ?subject for highlighting.
  const wrapped = `WHERE {\n${body}\n}`;
  const usedPrefixes = scanPrefixes(wrapped);
  const prefixes = prefixBlock(usedPrefixes);
  const query = `${prefixes}\n\nASK\n${wrapped}\n`;
  return { query, form: "ASK" };
}
